import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  getFactoryProductionMeta,
  getStageCapacities,
  upsertStageCapacity,
  getProductionEntries,
  createProductionEntry,
  updateProductionEntry,
  updatePlan,
  deletePlan,
  deleteProductionEntry,
  searchFactoryProjects,
  buildFactoryExportUrl,
  getFactoryDailyTotals,
  markProductionEntryDone,
} from "../api/service";


const STAGES_FALLBACK = [
  { value: "pressing", label: "Pressing" },
  { value: "cutting", label: "Cutting" },
  { value: "edge_banding", label: "Edge Banding" },
  { value: "boring", label: "Boring" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstDayThisMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};


function ProjectAutocomplete({ value, onChange, placeholder, size = "small" }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 1) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchFactoryProjects(query);
        setOptions(res?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  return (
    <Autocomplete
      size={size}
      options={options}
      getOptionLabel={(o) => o?.display || ""}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      value={value}
      onChange={(_, val) => onChange(val)}
      inputValue={query}
      onInputChange={(_, val) => setQuery(val)}
      loading={loading}
      noOptionsText={query ? "No matches" : "Type to search…"}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder || "Type project id or name"} variant="outlined" />
      )}
    />
  );
}


function CapacityBar({ stages, canEditCapacity, capacities, onChanged }) {
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState("");

  const capByStage = useMemo(() => {
    const m = {};
    (capacities || []).forEach((c) => { m[c.stage] = c; });
    return m;
  }, [capacities]);

  const save = async (stage) => {
    const val = parseInt(editing[stage], 10);
    if (Number.isNaN(val) || val < 0) {
      setErr("Capacity must be a non-negative number.");
      return;
    }
    setErr("");
    setSaving(stage);
    try {
      await upsertStageCapacity({ stage, daily_capacity: val });
      setEditing((e) => { const n = { ...e }; delete n[stage]; return n; });
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e?.message || "Failed to save capacity.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body py-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0">Daily Capacity (sheets / day)</h6>
          {!canEditCapacity && (
            <small className="text-muted">Only admins can edit capacity</small>
          )}
        </div>
        {err && <div className="alert alert-danger py-1 mb-2">{err}</div>}
        <div className="row g-2">
          {stages.map((s) => {
            const cap = capByStage[s.value];
            const current = cap?.daily_capacity ?? 0;
            const isEditing = editing[s.value] !== undefined;
            return (
              <div className="col-6 col-md-3" key={s.value}>
                <div className="border rounded p-2">
                  <div className="small text-muted">{s.label}</div>
                  {isEditing ? (
                    <div className="d-flex gap-1 mt-1">
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        value={editing[s.value]}
                        onChange={(e) => setEditing((x) => ({ ...x, [s.value]: e.target.value }))}
                      />
                      <button className="btn btn-sm btn-primary" disabled={saving === s.value} onClick={() => save(s.value)}>
                        {saving === s.value ? "…" : "Save"}
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing((e) => { const n = { ...e }; delete n[s.value]; return n; })}>
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between mt-1">
                      <div className="fs-5 fw-semibold">{current}</div>
                      {canEditCapacity && (
                        <button className="btn btn-sm btn-link p-0" onClick={() => setEditing((e) => ({ ...e, [s.value]: String(current) }))}>
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function AddEntryForm({ stages, pressingTypes, onCreated }) {
  const [date, setDate] = useState(todayStr());
  const [stage, setStage] = useState(stages[0]?.value || "pressing");
  const [pressingType, setPressingType] = useState("modular");
  const [sheets, setSheets] = useState("");
  const [notes, setNotes] = useState("");
  const [project, setProject] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const isPressing = stage === "pressing";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    if (!project) {
      setErr("Please select a project.");
      return;
    }
    const sheetsInt = parseInt(sheets, 10);
    if (Number.isNaN(sheetsInt) || sheetsInt <= 0) {
      setErr("Sheets must be a positive number.");
      return;
    }
    if (isPressing && !pressingType) {
      setErr("Please select a pressing type.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        date, stage, project: project.id, sheets: sheetsInt, notes,
      };
      if (isPressing) {
        payload.pressing_type = pressingType;
      }
      const resp = await createProductionEntry(payload);
      // When pressing is Modular, backend returns {entries:[...], plan_id}.
      // Manual pressing returns the same shape with 1 entry. Non-pressing
      // returns a single entry object. Show a helpful message either way.
      if (isPressing && pressingType === "modular") {
        const n = Array.isArray(resp?.entries) ? resp.entries.length : 0;
        setOk(n > 1 ? `Plan created (${n} entries: pressing + cutting + edge banding + boring).` : "Entry added.");
      } else {
        setOk("Entry added.");
      }
      setSheets("");
      setNotes("");
      setProject(null);
      if (onCreated) onCreated();
    } catch (e) {
      setErr(e?.message || "Failed to create entry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6 className="mb-3">Add Production Plan Entry</h6>
        {err && <div className="alert alert-danger py-1">{err}</div>}
        {ok && <div className="alert alert-success py-1">{ok}</div>}
        <form onSubmit={submit} className="row g-2 align-items-end">
          <div className="col-md-2">
            <label className="form-label small mb-1">Date</label>
            <input type="date" className="form-control form-control-sm" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="col-md-2">
            <label className="form-label small mb-1">Stage</label>
            <select className="form-select form-select-sm" value={stage} onChange={(e) => setStage(e.target.value)}>
              {stages.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          {isPressing && (
            <div className="col-md-2">
              <label className="form-label small mb-1">Pressing Type</label>
              <select
                className="form-select form-select-sm"
                value={pressingType}
                onChange={(e) => setPressingType(e.target.value)}
                required
              >
                {(pressingTypes && pressingTypes.length ? pressingTypes : [
                  { value: "manual", label: "Manual" },
                  { value: "modular", label: "Modular" },
                ]).map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className={isPressing ? "col-md-4" : "col-md-4"}>
            <label className="form-label small mb-1">Project</label>
            <ProjectAutocomplete value={project} onChange={setProject} />
          </div>
          <div className={isPressing ? "col-md-1" : "col-md-2"}>
            <label className="form-label small mb-1">Sheets</label>
            <input type="number" min="1" className="form-control form-control-sm" value={sheets} onChange={(e) => setSheets(e.target.value)} required />
          </div>
          <div className={isPressing ? "col-md-1" : "col-md-2"}>
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={submitting}>
              {submitting ? "Saving…" : "Add Entry"}
            </button>
          </div>
          <div className="col-12">
            <label className="form-label small mb-1">Notes (optional)</label>
            <input type="text" className="form-control form-control-sm" maxLength={255} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </form>
      </div>
    </div>
  );
}


function EditPlanModal({ entry, onClose, onSaved }) {
  const [date, setDate] = useState(entry.date || "");
  const [sheets, setSheets] = useState(String(entry.planned_sheets || entry.sheets || 0));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const n = parseInt(sheets, 10);
    if (Number.isNaN(n) || n <= 0) {
      setErr("Sheets must be a positive number.");
      return;
    }
    if (!date) {
      setErr("Pressing date is required.");
      return;
    }
    setSubmitting(true);
    try {
      await updatePlan(entry.id, { date, sheets: n });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setErr(e?.message || "Failed to update plan.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">Edit Plan</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-1">{err}</div>}
              <div className="mb-2 small text-muted">
                Project: {entry.project_display || entry.project}
              </div>
              <div className="mb-2 small text-muted">
                Pressing Type: {entry.pressing_type_display || "—"}
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">Pressing Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={date}
                  onChange={(ev) => setDate(ev.target.value)}
                  required
                />
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">Planned Sheets</label>
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  value={sheets}
                  onChange={(ev) => setSheets(ev.target.value)}
                  required
                />
              </div>
              <div className="small text-muted">
                Downstream rows (cutting, edge banding, boring) will be regenerated to match.
                If any row in this plan is already marked done, the update will be refused.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function MarkDoneModal({ entry, onClose, onDone }) {
  const [processed, setProcessed] = useState(String(entry.planned_sheets || entry.sheets || 0));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const planned = entry.planned_sheets || entry.sheets || 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const n = parseInt(processed, 10);
    if (Number.isNaN(n) || n < 0) {
      setErr("Processed must be a non-negative number.");
      return;
    }
    if (n > planned) {
      setErr(`Processed cannot exceed planned (${planned}).`);
      return;
    }
    setSubmitting(true);
    try {
      await markProductionEntryDone(entry.id, { processed_sheets: n });
      if (onDone) onDone();
    } catch (e) {
      setErr(e?.message || "Failed to mark done.");
    } finally {
      setSubmitting(false);
    }
  };

  const shortfall = Math.max(planned - (parseInt(processed, 10) || 0), 0);

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }} tabIndex="-1">
      <div className="modal-dialog modal-sm">
        <div className="modal-content">
          <form onSubmit={submit}>
            <div className="modal-header py-2">
              <h6 className="modal-title mb-0">Mark Done</h6>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-1">{err}</div>}
              <div className="small text-muted">Date · Stage · Project</div>
              <div className="mb-2">{entry.date} · {entry.stage_display || entry.stage} · {entry.project_display || entry.project}</div>
              <div className="mb-2">
                <label className="form-label small mb-1">Planned sheets</label>
                <input type="number" className="form-control form-control-sm" value={planned} readOnly />
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">Actually processed</label>
                <input type="number" min="0" max={planned} className="form-control form-control-sm" value={processed} onChange={(e) => setProcessed(e.target.value)} required autoFocus />
              </div>
              {shortfall > 0 && (
                <div className="alert alert-info py-1 small mb-0">
                  {shortfall} sheet{shortfall === 1 ? "" : "s"} will roll over to the next working day (respecting capacity).
                </div>
              )}
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : "Mark Done"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function DailyTotalsTable({ stages, dateFrom, dateTo, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [viewMode, setViewMode] = useState("planned");

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setErr("");
    try {
      const res = await getFactoryDailyTotals({ date_from: dateFrom, date_to: dateTo });
      setRows(res?.results || []);
    } catch (e) {
      setErr(e?.message || "Failed to load daily totals.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const cellValue = (row, stageValue) => {
    if (viewMode === "processed") {
      return row?.processed?.[stageValue] ?? 0;
    }
    return row?.planned?.[stageValue] ?? 0;
  };

  const totalValue = (row) => {
    if (viewMode === "processed") {
      return row?.processed_total ?? 0;
    }
    return row?.planned_total ?? 0;
  };

  return (
    <div>
      <div className="mb-2">
        <div className="btn-group btn-group-sm" role="group">
          <button
            className={"btn " + (viewMode === "planned" ? "btn-primary" : "btn-outline-secondary")}
            onClick={() => setViewMode("planned")}
          >
            Planned
          </button>
          <button
            className={"btn " + (viewMode === "processed" ? "btn-primary" : "btn-outline-secondary")}
            onClick={() => setViewMode("processed")}
          >
            Processed
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-1">{err}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              {stages.map((s) => (
                <th key={s.value} className="text-end">{s.label}</th>
              ))}
              <th className="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={stages.length + 2} className="text-center text-muted py-3">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={stages.length + 2} className="text-center text-muted py-3">No entries in this range.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>
                {stages.map((s) => (
                  <td key={s.value} className="text-end">{cellValue(r, s.value)}</td>
                ))}
                <td className="text-end fw-semibold">{totalValue(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function EntriesTable({
  stages, canEdit, refreshKey, onChanged,
  dateFrom, dateTo, setDateFrom, setDateTo,
  stageFilter, setStageFilter,
  projectFilter, setProjectFilter,
  page, setPage,
}) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [markingEntry, setMarkingEntry] = useState(null);

  const stageLabel = useMemo(() => {
    const m = {};
    stages.forEach((s) => { m[s.value] = s.label; });
    return m;
  }, [stages]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await getProductionEntries({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        stage: stageFilter || undefined,
        project: projectFilter?.id || undefined,
        page,
        page_size: pageSize,
      });
      setRows(res?.results || []);
      setCount(res?.count || 0);
    } catch (e) {
      setErr(e?.message || "Failed to load entries.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, stageFilter, projectFilter, page, pageSize]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this entry? If it has rollover rows, those will be deleted too.")) return;
    try {
      await deleteProductionEntry(id);
      if (onChanged) onChanged();
      load();
    } catch (e) {
      alert(e?.message || "Failed to delete.");
    }
  };

  const [editingPlan, setEditingPlan] = useState(null);

  const deletePlanClick = async (row) => {
    if (!window.confirm("Delete this ENTIRE plan (pressing + all downstream rows, including any done rows and rollovers)? This cannot be undone.")) return;
    try {
      const resp = await deletePlan(row.id);
      alert(`Deleted ${resp?.deleted ?? "?"} rows.`);
      if (onChanged) onChanged();
      load();
    } catch (e) {
      alert(e?.message || "Failed to delete plan.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label small mb-1">From</label>
          <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-1">To</label>
          <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }} />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-1">Stage</label>
          <select className="form-select form-select-sm" value={stageFilter} onChange={(e) => { setPage(1); setStageFilter(e.target.value); }}>
            <option value="">All stages</option>
            {stages.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small mb-1">Project</label>
          <ProjectAutocomplete
            value={projectFilter}
            onChange={(v) => { setPage(1); setProjectFilter(v); }}
            placeholder="All projects — type to filter"
          />
        </div>
      </div>

      {err && <div className="alert alert-danger py-1">{err}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Stage</th>
              <th>Project</th>
              <th className="text-end">Planned</th>
              <th className="text-end">Processed</th>
              <th>Notes</th>
              <th>Added by</th>
              {canEdit && <th style={{ width: "180px" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-muted py-3">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-muted py-3">No entries match.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const isRollover = !!r.rollover_from;
              const isDone = !!r.done_at;
              const rowStyle = isRollover ? { fontStyle: "italic", color: "#666" } : {};
              return (
                <tr key={r.id} style={rowStyle}>
                  <td>{r.date}</td>
                  <td>{stageLabel[r.stage] || r.stage_display || r.stage}</td>
                  <td>
                    {isRollover && <span title={r.rollover_reason || "Rollover"} style={{ marginRight: 4 }}>↪</span>}
                    {r.project_display || r.project}
                  </td>
                  <td className="text-end">{r.planned_sheets ?? r.sheets}</td>
                  <td className="text-end">
                    {r.processed_sheets != null ? (
                      <span>
                        {r.processed_sheets}
                        {isDone && <span className="ms-1 badge bg-success" style={{ fontWeight: 400 }}>done</span>}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-muted small">{
                    // Show type label (Pressing/Cutting sub-type) prefixed, then user notes.
                    // Rollover is already indicated by the ↪ arrow in the Project column.
                    (() => {
                      const typeLabel = r.pressing_type_display || r.cutting_type_display || "";
                      const userNote = r.notes || "";
                      if (typeLabel && userNote) return `${typeLabel} \u00B7 ${userNote}`;
                      if (typeLabel) return typeLabel;
                      if (userNote) return userNote;
                      return "—";
                    })()
                  }</td>
                  <td className="text-muted small">{r.created_by_name || "—"}</td>
                  {canEdit && (
                    <td>
                      {!isDone && (
                        <button className="btn btn-link btn-sm p-0 me-2" onClick={() => setMarkingEntry(r)}>Mark done</button>
                      )}
                      {!isDone && r.stage === "pressing" && r.plan_id && (
                        <button className="btn btn-link btn-sm p-0 me-2" onClick={() => setEditingPlan(r)}>Edit</button>
                      )}
                      {r.stage === "pressing" && r.plan_id && (
                        <button className="btn btn-link btn-sm text-danger p-0 me-2" onClick={() => deletePlanClick(r)}>Delete Plan</button>
                      )}
                      <button className="btn btn-link btn-sm text-danger p-0" onClick={() => deleteRow(r.id)}>Delete</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small className="text-muted">{count} total entries</small>
          <div className="btn-group btn-group-sm">
            <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <button className="btn btn-outline-secondary" disabled>{page} / {totalPages}</button>
            <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {markingEntry && (
        <MarkDoneModal
          entry={markingEntry}
          onClose={() => setMarkingEntry(null)}
          onDone={() => {
            setMarkingEntry(null);
            if (onChanged) onChanged();
            load();
          }}
        />
      )}
      {editingPlan && (
        <EditPlanModal
          entry={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSaved={() => {
            setEditingPlan(null);
            if (onChanged) onChanged();
            load();
          }}
        />
      )}
    </div>
  );
}


export default function FactoryProduction() {
  const [meta, setMeta] = useState(null);
  const [capacities, setCapacities] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [err, setErr] = useState("");
  const [activeTab, setActiveTab] = useState("entries");
  const [dateFrom, setDateFrom] = useState(firstDayThisMonth());
  const [dateTo, setDateTo] = useState(todayStr());
  // Lifted from EntriesTable so filters persist across tab switches.
  const [entriesStageFilter, setEntriesStageFilter] = useState("");
  const [entriesProjectFilter, setEntriesProjectFilter] = useState(null);
  const [entriesPage, setEntriesPage] = useState(1);

  const stages = meta?.stages?.length ? meta.stages : STAGES_FALLBACK;

  const loadMeta = useCallback(async () => {
    try {
      const res = await getFactoryProductionMeta();
      setMeta(res || null);
    } catch (e) {
      setErr(e?.message || "Failed to load permissions.");
    }
  }, []);

  const loadCaps = useCallback(async () => {
    try {
      const res = await getStageCapacities();
      setCapacities(res?.results || []);
    } catch {}
  }, []);

  useEffect(() => { loadMeta(); loadCaps(); }, [loadMeta, loadCaps]);

  const exportCsv = () => {
    if (!dateFrom || !dateTo) {
      alert("Please pick a date range to export.");
      return;
    }
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const url = buildFactoryExportUrl(dateFrom, dateTo);
    fetch(url, { headers: token ? { Authorization: token } : {} })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `factory_production_${dateFrom}_to_${dateTo}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => alert("Export failed. Please try again."));
  };

  if (!meta) return <div className="container-fluid p-3">Loading…</div>;
  if (!meta.can_view) {
    return (
      <div className="container-fluid p-3">
        <div className="alert alert-warning">You do not have access to Factory Production.</div>
      </div>
    );
  }

  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <div className="container-fluid p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Factory Production</h4>
        <small className="text-muted">Sequential stages: Pressing → Cutting → Edge Banding → Boring</small>
      </div>

      {err && <div className="alert alert-danger py-1">{err}</div>}

      <CapacityBar
        stages={stages}
        canEditCapacity={meta.can_edit_capacity}
        capacities={capacities}
        onChanged={loadCaps}
      />

      {meta.can_edit && (
        <AddEntryForm
          stages={stages}
          pressingTypes={meta?.pressing_types || []}
          onCreated={bump}
        />
      )}

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <ul className="nav nav-tabs" style={{ borderBottom: "none" }}>
              <li className="nav-item">
                <button
                  className={"nav-link " + (activeTab === "entries" ? "active" : "")}
                  onClick={() => setActiveTab("entries")}
                >
                  Entries
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={"nav-link " + (activeTab === "daily" ? "active" : "")}
                  onClick={() => setActiveTab("daily")}
                >
                  Daily Totals
                </button>
              </li>
            </ul>
            <button className="btn btn-outline-primary btn-sm" onClick={exportCsv}>
              Download CSV
            </button>
          </div>

          {activeTab === "entries" && (
            <EntriesTable
              stages={stages}
              canEdit={meta.can_edit}
              refreshKey={refreshKey}
              onChanged={bump}
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateFrom={setDateFrom}
              setDateTo={setDateTo}
              stageFilter={entriesStageFilter}
              setStageFilter={setEntriesStageFilter}
              projectFilter={entriesProjectFilter}
              setProjectFilter={setEntriesProjectFilter}
              page={entriesPage}
              setPage={setEntriesPage}
            />
          )}

          {activeTab === "daily" && (
            <div>
              <div className="row g-2 align-items-end mb-3">
                <div className="col-auto">
                  <label className="form-label small mb-1">From</label>
                  <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">To</label>
                  <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </div>
              <DailyTotalsTable
                stages={stages}
                dateFrom={dateFrom}
                dateTo={dateTo}
                refreshKey={refreshKey}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
